import Modal from "@via/components/modal";
import { useCreateOrganization } from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { ModalSize } from "@via/utils/constants";
import { useState, type JSX } from "react";

const INPUT_CLASS =
  "mt-2 block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";

/**
 * Creates an organization and makes the owner its org_admin. Left blank, the owner is the
 * administrator creating it. Members beyond the owner are added afterwards from Manage.
 */
export default function NewOrganizationModal({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const { mutateAsync: createOrganization, isPending } =
    useCreateOrganization();

  const handleConfirm = async (): Promise<boolean> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Name is required");
      return false;
    }
    const trimmedOwner = ownerEmail.trim();
    try {
      await createOrganization({
        name: trimmedName,
        ...(trimmedOwner ? { ownerEmail: trimmedOwner } : {}),
      });
      return true;
    } catch {
      // The hook already shows the server's reason. Keeping the modal open keeps the input.
      return false;
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={setIsOpen}
      title="New Organization"
      size={ModalSize.Small}
      confirmText="Create"
      isConfirmLoading={isPending}
      onConfirm={handleConfirm}>
      <div className="space-y-4">
        <div>
          <label
            htmlFor="new-organization-name"
            className="block text-sm font-medium text-gray-900">
            Name
          </label>
          <input
            id="new-organization-name"
            type="text"
            maxLength={255}
            value={name}
            onChange={e => {
              setName(e.target.value);
              setNameError(null);
            }}
            className={INPUT_CLASS}
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-600">{nameError}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="new-organization-owner"
            className="block text-sm font-medium text-gray-900">
            Owner email
          </label>
          <input
            id="new-organization-owner"
            type="email"
            value={ownerEmail}
            onChange={e => setOwnerEmail(e.target.value)}
            placeholder="person@example.edu"
            className={INPUT_CLASS}
          />
          <p className="mt-1 text-sm text-gray-500">
            Must be an existing account. It becomes the organization admin.
            Leave blank to own it yourself.
          </p>
        </div>
      </div>
    </Modal>
  );
}
